import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import {
  getRegiaoMetropolitana,
  normalizeCidade,
  getVizinhasNormalizadas,
  isBairroReal,
} from "@/lib/regioes-metropolitanas";

export interface ClusterPage {
  slug: string;
  cidade: string | null;
  bairro: string | null;
  preposicao_cidade: string | null;
  preposicao_bairro: string | null;
  palavra_chave: string | null;
  title: string | null;
}

function locationLabel(p: ClusterPage): string {
  if (p.bairro && p.bairro.trim()) return p.bairro;
  if (p.cidade && p.cidade.trim()) return p.cidade;
  return p.title ?? p.slug;
}

function locationPreposition(p: ClusterPage): string {
  if (p.bairro && p.bairro.trim()) return p.preposicao_bairro || "em";
  return p.preposicao_cidade || "em";
}

function sortAlpha(pages: ClusterPage[]): ClusterPage[] {
  return [...pages].sort((a, b) =>
    locationLabel(a).localeCompare(locationLabel(b), "pt-BR"),
  );
}

function LinkCard({ p }: { p: ClusterPage }) {
  const label = locationLabel(p);
  const prep = locationPreposition(p);
  return (
    <li key={p.slug}>
      <Link
        to="/$slug"
        params={{ slug: p.slug }}
        className="group flex h-full items-start justify-between gap-3 rounded-lg border border-border/70 bg-card px-4 py-3.5 transition-colors hover:border-brand-orange hover:bg-brand-orange/5"
      >
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-foreground">
            {prep} {label}
          </div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            Mão de obra temporária
          </div>
        </div>
        <ArrowUpRight
          aria-hidden
          className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-brand-orange"
        />
      </Link>
    </li>
  );
}

function Grid({ pages }: { pages: ClusterPage[] }) {
  return (
    <ul className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
      {pages.map((p) => (
        <LinkCard key={p.slug} p={p} />
      ))}
    </ul>
  );
}

export default function ClusterLinks({
  pages,
  currentCidade,
  segmento,
  title,
}: {
  pages: ClusterPage[];
  currentCidade?: string | null;
  segmento?: string | null;
  title?: string;
}) {
  if (!pages || pages.length === 0) return null;

  // Cidade-base: cidade da página atual, ou a cidade-base da região metropolitana que a contém.
  // Para landings antigas só com bairro (cidade vazia), assume "São Paulo".
  const cidadeBase =
    getRegiaoMetropolitana(currentCidade) ??
    (currentCidade && currentCidade.trim() ? currentCidade : "São Paulo");
  const baseNorm = normalizeCidade(cidadeBase);
  const vizinhasNorm = new Set(getVizinhasNormalizadas(cidadeBase));

  const bairrosDaBase: ClusterPage[] = [];
  const regiaoMetropolitana: ClusterPage[] = [];
  const outrasCidades: ClusterPage[] = [];

  for (const p of pages) {
    const cidadeNorm = normalizeCidade(p.cidade);
    // Landings antigas sem cidade preenchida assumem a cidade-base atual
    const cidadeParaChecar = p.cidade && p.cidade.trim() ? p.cidade : cidadeBase;
    const cidadeEfetivaNorm = cidadeNorm || baseNorm;
    const bairroReal = isBairroReal(cidadeParaChecar, p.bairro);

    // 1. Bairro real da cidade-base → Bairros de {cidade-base}
    if (bairroReal && cidadeEfetivaNorm === baseNorm) {
      bairrosDaBase.push(p);
      continue;
    }
    // 2. Cidade está na Grande {cidade-base} (ou o bairro é "não-bairro" tipo ABC)
    if (vizinhasNorm.has(cidadeEfetivaNorm)) {
      regiaoMetropolitana.push(p);
      continue;
    }
    if (!bairroReal && cidadeEfetivaNorm === baseNorm && p.bairro && p.bairro.trim()) {
      // "não-bairro" da cidade-base (ex.: ABC em São Paulo) → região metropolitana
      regiaoMetropolitana.push(p);
      continue;
    }
    // 3. Qualquer outra cidade (interior, outros estados sem RM mapeada)
    outrasCidades.push(p);
  }

  const heading = title ?? "Também atendemos nestas regiões";
  const subtitle = segmento
    ? `Mão de obra temporária para ${segmento}`
    : "Mão de obra temporária para construção civil";

  const groups = [
    { key: "bairros", label: `Bairros de ${cidadeBase}`, items: sortAlpha(bairrosDaBase) },
    { key: "regiao", label: `Região metropolitana de ${cidadeBase}`, items: sortAlpha(regiaoMetropolitana) },
    { key: "outras", label: "Outras cidades atendidas", items: sortAlpha(outrasCidades) },
  ].filter((g) => g.items.length > 0);

  if (groups.length === 0) return null;

  return (
    <section className="border-t border-border/60 bg-background py-16 md:py-20">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-brand-orange">
            {subtitle}
          </p>
          <h2 className="mt-3 font-display text-3xl font-bold leading-[1.1] tracking-[-0.02em] text-foreground md:text-4xl">
            {heading}
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Equipes qualificadas mobilizadas em até 7 dias úteis em toda a região.
          </p>
        </div>

        <div className="space-y-10">
          {groups.map((g) => (
            <div key={g.key}>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {g.label}
              </h3>
              <Grid pages={g.items} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}